import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY_PREFIX = "variant_preview_mode_"

/**
 * Preview Spin Controller
 *
 * Manages the variant preview card: Spin/Grid mode switching with localStorage
 * persistence, grid thumbnail highlight on click, gallery preview overlay,
 * and altTextUpdated tooltip refresh.
 *
 * Exposes window.switchPreviewMode(mode) and window.showGalleryPreview(url)
 * for use from nav links and statistics view.
 *
 * Usage: data-controller="preview-spin" data-preview-spin-variant-uuid-value="..."
 * on the preview card (#preview-card).
 */
export default class extends Controller {
  static values = {
    variantUuid: String
  }

  connect() {
    this.boundPreviewModeClick = this.previewModeClick.bind(this)
    this.boundGridThumbClick = this.gridThumbClick.bind(this)
    this.boundGalleryCloseClick = this.galleryCloseClick.bind(this)
    this.boundAltTextUpdated = this.handleAltTextUpdated.bind(this)

    document.addEventListener("click", this.boundPreviewModeClick)
    document.addEventListener("click", this.boundGridThumbClick)
    document.addEventListener("click", this.boundGalleryCloseClick)
    document.addEventListener("altTextUpdated", this.boundAltTextUpdated)

    window.switchPreviewMode = (mode) => this.applyMode(mode)
    window.showGalleryPreview = (url) => this.showGalleryPreview(url)

    this.restoreMode()
  }

  disconnect() {
    document.removeEventListener("click", this.boundPreviewModeClick)
    document.removeEventListener("click", this.boundGridThumbClick)
    document.removeEventListener("click", this.boundGalleryCloseClick)
    document.removeEventListener("altTextUpdated", this.boundAltTextUpdated)
    delete window.switchPreviewMode
    delete window.showGalleryPreview
  }

  getStorageKey() {
    const uuid = this.variantUuidValue
    return uuid ? STORAGE_KEY_PREFIX + uuid : null
  }

  previewModeClick(e) {
    const btn = e.target.closest && e.target.closest("[data-preview-mode]")
    if (!btn) return
    e.preventDefault()
    const mode = btn.getAttribute("data-preview-mode")
    if (mode) this.applyMode(mode)
  }

  applyMode(mode) {
    const preview = this.element.querySelector("#preview")
    if (!preview) return

    const cardBody = preview.closest(".card-body")
    const iframe = preview.querySelector("iframe")
    const preview360 = this.element.querySelector("#preview-360")
    const grid = this.element.querySelector("#preview-grid")
    const galleryDetail = this.element.querySelector("#gallery-preview")

    if (iframe) iframe.style.display = "none"
    if (preview360) preview360.style.display = "none"
    if (grid) grid.style.display = "none"
    if (galleryDetail) galleryDetail.style.display = "none"

    if (mode === "grid" && grid) {
      preview.classList.add("grid-mode")
      if (cardBody) {
        cardBody.classList.add("grid-mode")
        cardBody.style.height = "auto"
      }
      const card = this.element
      if (card) card.style.height = "auto"
      grid.style.display = "block"
      this.initializeGridTooltips()
    } else {
      preview.classList.remove("grid-mode")
      if (cardBody) {
        cardBody.classList.remove("grid-mode")
        cardBody.style.height = ""
      }
      const card = this.element
      if (card) card.style.height = "calc(100vh - 200px)"
      if (preview360) preview360.style.display = "block"
      if (iframe) iframe.style.display = "block"
    }

    const storageKey = this.getStorageKey()
    if (storageKey && typeof localStorage !== "undefined") {
      try {
        localStorage.setItem(storageKey, mode)
      } catch (err) {}
    }
  }

  restoreMode() {
    let savedMode = null
    const storageKey = this.getStorageKey()
    if (storageKey && typeof localStorage !== "undefined") {
      try {
        savedMode = localStorage.getItem(storageKey)
      } catch (err) {}
    }
    let modeToApply = savedMode
    if (!modeToApply && typeof window.location !== "undefined" && window.location.pathname.indexOf("embed_image") !== -1) {
      modeToApply = "grid"
    }
    if (modeToApply) this.applyMode(modeToApply)
  }

  gridThumbClick(e) {
    const grid = this.element.querySelector("#preview-grid")
    if (!grid) return
    const link = e.target.closest && e.target.closest('#preview-grid a[href*="embed_image"]')
    if (!link) return
    const wrapper = link.closest(".p-1")
    if (!wrapper) return
    grid.querySelectorAll(".grid-thumb-selected").forEach((el) => {
      el.classList.remove("grid-thumb-selected")
    })
    wrapper.classList.add("grid-thumb-selected")
  }

  galleryCloseClick(e) {
    if (e.target && e.target.id === "gallery-preview-close") {
      e.preventDefault()
      this.applyMode("360")
    }
  }

  showGalleryPreview(url) {
    this.applyMode("grid")

    const gallery = this.element.querySelector("#gallery-preview")
    const img = this.element.querySelector("#gallery-preview-image")
    const linkEl = this.element.querySelector("#gallery-preview-url")
    if (!gallery || !img || !linkEl) return

    img.src = url
    linkEl.textContent = url
    linkEl.href = url
    gallery.style.display = "block"
  }

  initializeGridTooltips() {
    const grid = this.element.querySelector("#preview-grid")
    if (!grid) return
    const gridImages = grid.querySelectorAll('img[data-toggle="tooltip"]')
    if (gridImages.length === 0) return

    if (typeof window.$ !== "undefined" && window.$.fn.tooltip) {
      window.$(gridImages).tooltip()
    } else if (typeof bootstrap !== "undefined" && bootstrap.Tooltip) {
      gridImages.forEach((img) => {
        const existing = bootstrap.Tooltip.getInstance(img)
        if (existing) existing.dispose()
        new bootstrap.Tooltip(img)
      })
    }
  }

  handleAltTextUpdated(e) {
    const imageId = e.detail.imageId
    const altTexts = e.detail.altTexts || {}
    const grid = this.element.querySelector("#preview-grid")
    if (!grid) return
    const gridImg = grid.querySelector(`img[data-image-id="${imageId}"]`)
    if (!gridImg) return

    const tooltipLines = Object.entries(altTexts)
      .filter(([, text]) => text && String(text).trim().length > 0)
      .map(([lang, text]) => `${lang.toUpperCase()}: ${text}`)
    const tooltipText = tooltipLines.join("\n")
    const tooltipHtml = tooltipLines.join("<br>")

    if (tooltipText.length > 0) {
      const resolvedAltText = tooltipLines[0].split(": ").slice(1).join(": ") || ""
      gridImg.setAttribute("alt", resolvedAltText)
      gridImg.setAttribute("title", tooltipHtml)
      gridImg.setAttribute("data-toggle", "tooltip")
      gridImg.setAttribute("data-placement", "top")
      gridImg.setAttribute("data-html", "true")
      gridImg.setAttribute("data-alt-texts", JSON.stringify(altTexts))

      if (typeof window.$ !== "undefined" && window.$.fn.tooltip) {
        window.$(gridImg).tooltip("dispose").attr("title", tooltipHtml).tooltip()
      } else if (typeof bootstrap !== "undefined" && bootstrap.Tooltip) {
        const tooltip = bootstrap.Tooltip.getInstance(gridImg)
        if (tooltip) {
          tooltip.setContent({ ".tooltip-inner": tooltipHtml })
        } else {
          new bootstrap.Tooltip(gridImg)
        }
      }
    } else {
      gridImg.setAttribute("alt", "")
      gridImg.removeAttribute("title")
      gridImg.removeAttribute("data-toggle")
      gridImg.removeAttribute("data-placement")
      gridImg.removeAttribute("data-html")
      gridImg.removeAttribute("data-alt-texts")

      if (typeof window.$ !== "undefined" && window.$.fn.tooltip) {
        window.$(gridImg).tooltip("dispose")
      } else if (typeof bootstrap !== "undefined" && bootstrap.Tooltip) {
        const tooltip = bootstrap.Tooltip.getInstance(gridImg)
        if (tooltip) tooltip.dispose()
      }
    }
  }
}
